import { useEffect, useState } from 'react';
import { X, FileText, Layers } from 'lucide-react';
import { getIntakeTemplate } from '../domain/besoins/templates';
import {
  CLIENT_2026_ALL_COL_DEFS,
  CLIENT_2026_AXE_TITLES,
  CLIENT_2026_ROWS,
} from '../domain/besoins/templates/prise-de-besoin-client-2026';

interface PriseDeBesoinModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'structure' | 'rapport';
  templateId?: string;
}

const client2026AxeSummaries = Object.entries(CLIENT_2026_AXE_TITLES)
  .map(([axe, title]) => ({
    axe: Number(axe),
    title,
    count: CLIENT_2026_ROWS.filter((row) => row.axe === Number(axe)).length,
  }));

const client2026WhenToUse = [
  'Avant une première analyse ou une rencontre stratégique client.',
  'Quand le client exprime plusieurs irritants sans ordre de priorité clair.',
  'Quand BZ doit préparer un Plan Directeur, une ODS, une roadmap, un budget ou une offre de service.',
  'Quand il faut documenter les décisions d\'un comité TI ou d\'une rencontre de direction.',
  'Quand une relation client doit passer d\'une gestion réactive à une trajectoire structurée.',
];

const client2026ProductionSteps = [
  { title: '1. Écouter et capter', text: 'Noter les réponses du client avec ses mots, sans trop interpréter au départ.' },
  { title: '2. Évaluer la maturité', text: 'Pour chaque ligne verte du fichier de référence, saisir les niveaux KMI As-Was, As-Is et To-Be selon le barème propre à la question.' },
  { title: '3. Classer par pilier', text: 'Rattacher chaque réponse à l’un des six piliers du cadre BZ.' },
  { title: '4. Traduire en analyse BZ', text: 'Ajouter priorité, objectif SMART, solution techno/processus/humain, risques, mitigations et besoins.' },
  { title: '5. Valider la lecture', text: 'Confirmer avec le client ou le Directeur TI que la maturité, la priorité et la compréhension sont justes.' },
  { title: '6. Alimenter les livrables', text: 'Utiliser le tableau comme source pour générer les documents plus formels: analyse, recommandations, roadmap, ODS, budget ou plan directeur.' },
];

const client2026Readers = [
  { role: 'Client / direction', needs: 'Comprendre ce que BZ a retenu, les priorités proposées et les prochaines décisions à prendre.' },
  { role: 'Directeur TI BZ', needs: 'Transformer les besoins en trajectoire, arbitrer les priorités et choisir les livrables pertinents.' },
  { role: 'Spécialistes BZ', needs: 'Voir rapidement les contraintes, risques, solutions attendues et informations manquantes.' },
  { role: 'Équipe livraison / projet', needs: 'Convertir les lignes en tâches, responsabilités, échéances et preuves de fermeture.' },
];

const client2026DerivedDeliverables = [
  'Analyse & recommandations',
  'Plan Directeur TI',
  'Feuille de route / roadmap',
  'Ébauche d\'ODS',
  'Plan budgétaire',
  'Registre de risques',
  'Backlog d\'amélioration continue',
  'Plan d\'actions comité TI',
];

const client2026DefinitionOfDone = [
  'Les réponses client importantes sont capturées et rattachées au bon pilier.',
  'Les priorités client et BZ sont explicites.',
  'Chaque ligne importante indique comment savoir que le problème est réglé.',
  'Les objectifs SMART et les solutions sont assez concrets pour produire un livrable.',
  'Les risques, mitigations et besoins restants sont visibles.',
  'Les informations manquantes sont nommées au lieu d\'être inventées.',
];

const kmiScoreScale = [
  { score: '0', meaning: 'On ne s\'en occupe pas', use: 'Exclu du calcul; signal à expliquer si le sujet est volontairement hors portée.' },
  { score: '1', meaning: 'Absent ou très faible', use: 'Risque important; action structurante à considérer.' },
  { score: '2', meaning: 'Présent mais informel', use: 'Pratique partielle; besoin de standardiser.' },
  { score: '3', meaning: 'Défini mais variable', use: 'Base exploitable; amélioration à planifier.' },
  { score: '4', meaning: 'Structuré et suivi', use: 'Bonne maîtrise; optimiser ou maintenir.' },
  { score: '5', meaning: 'Intégré et mesuré', use: 'Référence interne; levier de performance.' },
];

const kmiReaders = [
  { role: 'Client / direction', needs: 'Voir clairement la maturité actuelle par pilier, les écarts majeurs et les décisions à prendre.' },
  { role: 'Directeur TI BZ', needs: 'Prioriser les chantiers, préparer les recommandations et cadrer la trajectoire de maturité.' },
  { role: 'Équipe SA / livraison', needs: 'Comprendre les piliers faibles, les preuves manquantes et les actions à transformer en livrables.' },
  { role: 'PRIsME Client', needs: 'Alimenter le spider de maturité actuelle avec des moyennes fiables par pilier.' },
];

const kmiProductionSteps = [
  { title: '1. Qualifier', text: 'Expliquer l\'échelle 0 à 5 et confirmer que chaque réponse représente la réalité actuelle, pas le souhait.' },
  { title: '2. Coter', text: 'Répondre à chaque question par pilier; utiliser 0 seulement si le sujet est réellement non traité ou hors portée.' },
  { title: '3. Lire les écarts', text: 'Comparer les moyennes par pilier pour identifier les zones faibles, fortes et incohérentes.' },
  { title: '4. Prioriser', text: 'Transformer les écarts en enjeux d\'affaires, risques et chantiers recommandés.' },
  { title: '5. Alimenter PRIsME', text: 'Produire le diagnostic et pousser les scores vers le spider de maturité actuelle.' },
];

const kmiDerivedDeliverables = [
  'Diagnostic de maturité TI',
  'Spider maturité actuelle',
  'Analyse des écarts',
  'Plan Directeur TI',
  'Ébauche d\'ODS',
  'Roadmap d\'amélioration',
  'Registre de risques',
  'Priorités comité TI',
];

const kmiDefinitionOfDone = [
  'Les 6 piliers ont été cotés avec une réponse claire pour chaque question applicable.',
  'Les scores 0 sont justifiés ou assumés comme sujets non traités.',
  'Les moyennes par pilier sont interprétées, pas seulement affichées.',
  'Les écarts critiques sont reliés à des impacts d\'affaires ou opérationnels.',
  'Les recommandations restent proportionnées au niveau de maturité observé.',
  'Le diagnostic peut alimenter le spider PRIsME Client et les livrables suivants.',
];

export default function PriseDeBesoinModal({ isOpen, onClose, initialTab = 'structure', templateId = 'pdti-standard-pme' }: PriseDeBesoinModalProps) {
  const [activeTab, setActiveTab] = useState<'structure' | 'rapport'>(initialTab);
  const template = getIntakeTemplate(templateId);
  const isClient2026AnalysisTemplate = templateId === 'prise-de-besoin-client-2026-analyse';
  const isClient2026Template = templateId === 'prise-de-besoin-client-2026' || isClient2026AnalysisTemplate;
  const isKmiTemplate = templateId === 'prisme-kmi-v1' || templateId === 'prisme-kmi-cible-v1';
  const kmiPillarSummaries = isKmiTemplate
    ? template?.phases.map((phase) => ({
        title: phase.title.replace(/^Pilier\s+\d+\s+—\s+/, ''),
        count: phase.questions.length,
      })) || []
    : [];

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [initialTab, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6">
      <div className="w-[80vw] max-w-[1400px] max-h-[92vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-bz-dark via-bz-blue to-indigo-700 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shadow-inner">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {template?.name || 'Prise de Besoin Holistique – Cadre PRIsME'}
              </h2>
              <p className="text-[11px] text-white/80">
                {template?.description || '5 phases pour capturer tous les besoins du client, incluant le contexte métier'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white/80" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200 px-6 pt-3 pb-2 bg-gray-50 text-xs">
          <button
            onClick={() => setActiveTab('structure')}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-full font-medium transition-colors ${
              activeTab === 'structure'
                ? 'bg-bz-blue text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Layers className="w-4 h-4" />
            {isClient2026Template || isKmiTemplate ? 'Espace du document' : 'Structure des 5 phases'}
          </button>
          <button
            onClick={() => setActiveTab('rapport')}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-full font-medium transition-colors ${
              activeTab === 'rapport'
                ? 'bg-bz-blue text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FileText className="w-4 h-4" />
            {isClient2026Template || isKmiTemplate ? 'Document à produire' : 'Exemple de rapport (client fictif)'}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-gray-50 text-sm text-gray-700">
          {activeTab === 'structure' && templateId === 'politique-cybersecurite-v2' && (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Vision PRIsME de la prise de besoin - Politique de cybersécurité
                </h3>
                <p className="text-xs text-gray-600">
                  Ce questionnaire complet permet d'élaborer une politique de cybersécurité alignée sur <strong>NIST CSF 2.0</strong>, <strong>ITIL 4</strong>, <strong>COBIT 2019</strong> et <strong>ISO 27001</strong>. 
                  Il couvre tous les aspects essentiels : utilisations autorisées, logiciels, médias sociaux, BYOD, contrôles d'accès, authentification, mots de passe, courriel, restrictions et IA.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Phase 1 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-bz-blue uppercase mb-1">
                    Phase 1 sur 4
                  </p>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">Qualification & Contexte</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    Informations générales sur l'entreprise et exigences initiales.
                  </p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Nom de l'entreprise</li>
                    <li>Enquête de sécurité requise pour les usagers</li>
                    <li>Conformité réglementaire (Loi 25, RGPD, etc.)</li>
                    <li>Contexte organisationnel</li>
                    <li>Exigences initiales</li>
                  </ul>
                </div>

                {/* Phase 2 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-purple-600 uppercase mb-1">
                    Phase 2 sur 4
                  </p>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">Contrôles d'Accès & Utilisation</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    Gestion des accès, utilisations autorisées, logiciels, médias sociaux et BYOD.
                  </p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Utilisations autorisées des actifs TI</li>
                    <li>Gestion des logiciels et applications</li>
                    <li>Politique médias sociaux</li>
                    <li>Politique BYOD (Bring Your Own Device)</li>
                    <li>Contrôles d'accès réseau et systèmes</li>
                  </ul>
                </div>

                {/* Phase 3 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-indigo-600 uppercase mb-1">
                    Phase 3 sur 4
                  </p>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">Authentification & Sécurité</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    Authentification, mots de passe, courriel sécurisé et gestion des identités.
                  </p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Politique d'authentification (MFA, SSO)</li>
                    <li>Gestion des mots de passe</li>
                    <li>Sécurité du courriel</li>
                    <li>Gestion des identités et accès</li>
                    <li>Protection contre les menaces</li>
                  </ul>
                </div>

                {/* Phase 4 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-emerald-600 uppercase mb-1">
                    Phase 4 sur 4
                  </p>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">Politiques & Restrictions</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    Restrictions d'utilisation, politique IA, conformité et gouvernance.
                  </p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Restrictions d'utilisation spécifiques</li>
                    <li>Politique d'utilisation de l'IA</li>
                    <li>Conformité et audit</li>
                    <li>Gouvernance de la cybersécurité</li>
                    <li>Formation et sensibilisation</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'structure' && isClient2026Template && (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-emerald-200 p-4 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {isClient2026AnalysisTemplate
                    ? 'Espace du document - Prise de besoin - Maturité et besoins client - Analyse & recommandations'
                    : 'Espace du document - Prise de besoin - Maturité et besoins client (tableau)'}
                </h3>
                <p className="text-xs text-gray-600">
                  {isClient2026AnalysisTemplate
                    ? 'Cet espace explique comment produire une analyse narrative professionnelle à partir du formulaire 2026: constats, priorités, risques, recommandations et prochaines étapes, sans inventer de faits client.'
                    : 'Cet espace explique ce que le bouton doit piloter pour ce document: un tableau de prise de besoin structuré par piliers du cadre BZ. Les réponses saisies dans le formulaire deviennent directement les lignes du document produit, sans transformer le tout en rapport narratif.'}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-bz-blue uppercase mb-1">Esprit du document</p>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">
                    {isClient2026AnalysisTemplate ? 'Transformer les réponses en lecture exécutive' : 'Faire passer la rencontre en plan clair'}
                  </h4>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    {isClient2026AnalysisTemplate
                      ? 'Le document narratif ne reproduit pas seulement les réponses. Il met en évidence ce que BZ comprend, les écarts, les priorités, les risques et les recommandations qui méritent une décision.'
                      : 'Le tableau n\'est pas un simple formulaire. C\'est la traduction structurée de ce que le client dit, de ce que BZ comprend, et de ce qu\'il faut faire ensuite. Il sert à éviter les angles morts, garder une trace de la conversation et transformer les besoins en décisions, priorités et livrables.'}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-emerald-700 uppercase mb-1">À qui on le livre</p>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Client, Directeur TI BZ et équipe de livraison</h4>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    Il peut être partagé avec le client comme synthèse de la prise de besoin, puis utilisé à l'interne
                    par le Directeur TI BZ, les spécialistes et les responsables de livraison pour cadrer les actions,
                    préparer les documents dérivés et suivre les prochaines étapes.
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-indigo-700 uppercase mb-1">Pourquoi on le produit</p>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">
                    {isClient2026AnalysisTemplate ? 'Faire ressortir les recommandations' : 'Aligner besoin, priorité et valeur'}
                  </h4>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    {isClient2026AnalysisTemplate
                      ? 'Il donne une lecture claire à la direction: ce qui est connu, ce qui manque, ce qui est critique, ce qui est recommandé et ce qui doit être validé avant de produire les autres livrables.'
                      : 'Il donne une base commune pour décider quoi traiter, dans quel ordre, avec quelle justification et quel résultat attendu. Il alimente ensuite les documents à produire: analyse, recommandations, plan directeur, ODS, roadmap, budget, registre de risques ou backlog d\'amélioration.'}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Quand utiliser ce document?</h4>
                  <ul className="list-disc list-inside text-xs text-gray-700 space-y-1">
                    {client2026WhenToUse.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Ce que chaque lecteur doit y trouver</h4>
                  <div className="space-y-2">
                    {client2026Readers.map((reader) => (
                      <div key={reader.role} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <p className="text-[11px] font-semibold text-gray-900">{reader.role}</p>
                        <p className="text-[11px] text-gray-600 leading-snug">{reader.needs}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Méthode de production</h4>
                <div className="grid md:grid-cols-5 gap-3">
                  {client2026ProductionSteps.map((step) => (
                    <div key={step.title} className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-3">
                      <p className="text-[11px] font-bold text-emerald-900 mb-1">{step.title}</p>
                      <p className="text-[11px] text-gray-700 leading-snug">{step.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-indigo-950 mb-3">Documents que ce tableau peut alimenter</h4>
                  <div className="flex flex-wrap gap-2">
                    {client2026DerivedDeliverables.map((deliverable) => (
                      <span key={deliverable} className="rounded-full bg-white border border-indigo-200 px-3 py-1 text-[11px] font-medium text-indigo-800">
                        {deliverable}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-emerald-950 mb-3">Définition de “livrable prêt”</h4>
                  <ul className="list-disc list-inside text-xs text-gray-700 space-y-1">
                    {client2026DefinitionOfDone.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4">
                  <p className="text-[11px] font-semibold text-emerald-800 uppercase mb-1">Finalité</p>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Capturer la réalité client</h4>
                  <p className="text-xs text-gray-700">
                    Identifier les problèmes, besoins, souhaits, menaces, soucis et améliorations exprimés par le
                    client, puis les traduire en objectifs SMART, solutions, coûts, risques et mitigations.
                  </p>
                </div>
                <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                  <p className="text-[11px] font-semibold text-blue-800 uppercase mb-1">Source</p>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Formulaire tabulaire 2026</h4>
                  <p className="text-xs text-gray-700">
                    Le formulaire reprend les piliers et les questions du gabarit Excel 2026. Les cellules remplies
                    alimentent le document final ligne par ligne.
                  </p>
                </div>
                <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4">
                  <p className="text-[11px] font-semibold text-indigo-800 uppercase mb-1">Sortie</p>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">
                    {isClient2026AnalysisTemplate ? 'Analyse narrative' : 'Document tableau'}
                  </h4>
                  <p className="text-xs text-gray-700">
                    {isClient2026AnalysisTemplate
                      ? 'Le livrable attendu est une synthèse structurée: constats par pilier, priorités, risques, recommandations, bonnes pratiques clairement séparées et prochaines validations.'
                      : 'Le livrable attendu est une version propre du tableau client: réponses, priorités, preuve de règlement, plan d\'action, risques et besoins à produire.'}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Piliers couverts par le tableau</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {client2026AxeSummaries.map((axe) => (
                    <div key={axe.axe} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[11px] font-semibold text-bz-blue">Pilier {axe.axe}</p>
                          <p className="text-xs font-semibold text-gray-900">{axe.title}</p>
                        </div>
                        <span className="shrink-0 rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[10px] text-gray-600">
                          {axe.count} lignes
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Colonnes à piloter dans le document produit</h4>
                <div className="grid md:grid-cols-3 gap-2">
                  {CLIENT_2026_ALL_COL_DEFS.map((col) => (
                    <div key={col.key} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                      <p className="text-[10px] font-bold text-emerald-700 uppercase">Colonne {col.key}</p>
                      <p className="text-[11px] text-gray-700 leading-snug">{col.label}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-gray-500">
                  Les colonnes A à AE couvrent la classification STO, les six piliers, les questions, la maturité
                  As-Was / As-Is / To-Be, les barèmes, les réponses, les priorités et l'analyse nécessaire au document.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'structure' && isKmiTemplate && (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-blue-200 p-4 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Espace du document - PRIsME KMI (diagnostic 6 piliers)
                </h3>
                <p className="text-xs text-gray-600">
                  Cet espace explique comment produire un diagnostic de maturité actuelle à partir du questionnaire
                  KMI. Le but n'est pas de faire un sondage abstrait: les scores doivent révéler les écarts de maturité,
                  alimenter le spider PRIsME Client et guider les priorités de transformation.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-bz-blue uppercase mb-1">Esprit du document</p>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Mesurer pour décider, pas pour noter</h4>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    Le KMI sert à objectiver la maturité TI du client sur 6 piliers. Il donne une lecture commune des
                    forces, faiblesses et angles morts, puis transforme ces constats en décisions: quoi stabiliser,
                    quoi améliorer et quoi prioriser.
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-emerald-700 uppercase mb-1">À qui on le livre</p>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Direction client, Directeur TI BZ et PRIsME Client</h4>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    Le diagnostic est livré à la direction et aux décideurs client pour comprendre la posture actuelle.
                    Il sert aussi au Directeur TI BZ et à l'équipe de livraison pour cadrer les recommandations, les
                    chantiers et la représentation visuelle dans PRIsME Client.
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-indigo-700 uppercase mb-1">Pourquoi on le produit</p>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Relier maturité, risque et prochaine action</h4>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    Il permet d'expliquer pourquoi certains piliers doivent être traités avant d'autres. Le document
                    devient la base du diagnostic, du spider, des recommandations, du Plan Directeur TI ou d'une ODS.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Ce que chaque lecteur doit y trouver</h4>
                  <div className="space-y-2">
                    {kmiReaders.map((reader) => (
                      <div key={reader.role} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <p className="text-[11px] font-semibold text-gray-900">{reader.role}</p>
                        <p className="text-[11px] text-gray-600 leading-snug">{reader.needs}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Échelle de maturité 0 à 5</h4>
                  <div className="space-y-2">
                    {kmiScoreScale.map((step) => (
                      <div key={step.score} className="grid grid-cols-[36px_1fr] gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-bz-blue text-white text-[11px] font-bold">
                          {step.score}
                        </span>
                        <div>
                          <p className="text-[11px] font-semibold text-gray-900">{step.meaning}</p>
                          <p className="text-[11px] text-gray-600 leading-snug">{step.use}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Méthode de production</h4>
                <div className="grid md:grid-cols-5 gap-3">
                  {kmiProductionSteps.map((step) => (
                    <div key={step.title} className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-3">
                      <p className="text-[11px] font-bold text-blue-950 mb-1">{step.title}</p>
                      <p className="text-[11px] text-gray-700 leading-snug">{step.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">6 piliers couverts</h4>
                  <div className="grid gap-2">
                    {kmiPillarSummaries.map((pillar, index) => (
                      <div key={pillar.title} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <div>
                          <p className="text-[11px] font-semibold text-bz-blue">Pilier {index + 1}</p>
                          <p className="text-xs font-semibold text-gray-900">{pillar.title}</p>
                        </div>
                        <span className="shrink-0 rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[10px] text-gray-600">
                          {pillar.count} questions
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-indigo-950 mb-3">Documents que le KMI peut alimenter</h4>
                  <div className="flex flex-wrap gap-2">
                    {kmiDerivedDeliverables.map((deliverable) => (
                      <span key={deliverable} className="rounded-full bg-white border border-indigo-200 px-3 py-1 text-[11px] font-medium text-indigo-800">
                        {deliverable}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-emerald-950 mb-3">Définition de “livrable prêt”</h4>
                <ul className="list-disc list-inside text-xs text-gray-700 space-y-1">
                  {kmiDefinitionOfDone.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'structure' && templateId !== 'politique-cybersecurite-v2' && !isClient2026Template && !isKmiTemplate && (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Vision PRIsME de la prise de besoin
                </h3>
                <p className="text-xs text-gray-600">
                  La prise de besoin holistique PRIsME est pensée pour être <strong>unique</strong> par client, mais
                  suffisamment riche pour alimenter ensuite <strong>plusieurs types de documents</strong> (Plan
                  Directeur, ODS, Plan Cyber, etc.). Elle se compose de 4 phases génériques + 1 phase métier.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Phase 1 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-bz-blue uppercase mb-1">
                    Phase 1 sur 5
                  </p>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">Qualification Client</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    On pose le décor: qui est le client, quel est son secteur, sa taille, sa timeline et son budget
                    TI.
                  </p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Nom de l&apos;organisation, type d&apos;organisation</li>
                    <li>Industrie / secteur d&apos;activité</li>
                    <li>Taille (nb d&apos;employés, sites, localisation)</li>
                    <li>Horizon de réflexion (court / moyen / long terme)</li>
                    <li>Ordre de grandeur du budget TI envisagé</li>
                  </ul>
                </div>

                {/* Phase 2 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-purple-600 uppercase mb-1">
                    Phase 2 sur 5
                  </p>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">Contexte &amp; Objectifs</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    On comprend les enjeux, objectifs et métriques de succès pour les 12–24 prochains mois.
                  </p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Défi TI principal (texte libre détaillé)</li>
                    <li>Enjeux critiques et risques perçus</li>
                    <li>Objectifs d&apos;affaires (cases à cocher + précisions)</li>
                    <li>Objectifs TI (modernisation, sécurité, cloud, etc.)</li>
                    <li>Métriques de succès / KPIs attendus</li>
                  </ul>
                </div>

                {/* Phase 3 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-indigo-600 uppercase mb-1">
                    Phase 3 sur 5
                  </p>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">Besoins Techniques</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    On cartographie l&apos;infrastructure, les systèmes actuels et les besoins d&apos;intégration.
                  </p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Systèmes critiques actuels (CRM, comptabilité, RH, etc.)</li>
                    <li>Type d&apos;infrastructure (on-prem, cloud, hybride)</li>
                    <li>Besoins d&apos;intégration &amp; automatisation (no-code/low-code)</li>
                    <li>Points de douleur techniques (performance, lenteur, obsolescence)</li>
                    <li>Préoccupations sécurité &amp; conformité (backup, ransomware, Loi 25, etc.)</li>
                  </ul>
                </div>

                {/* Phase 4 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-emerald-600 uppercase mb-1">
                    Phase 4 sur 5
                  </p>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">Gouvernance &amp; Support</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    On clarifie qui décide, qui sera impacté et comment le changement sera supporté.
                  </p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Décideurs clés pour le projet (DG, CE, comité TI, etc.)</li>
                    <li>Parties prenantes principales (C-Suite, finances, opérations...)</li>
                    <li>Préparation au changement (réticence, ouverture, besoin d&apos;accompagnement)</li>
                    <li>Besoins de formation (de base, avancée, continue)</li>
                    <li>Modèle de support souhaité (réactif vs proactif)</li>
                  </ul>
                </div>

                {/* Phase 5 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm md:col-span-2">
                  <p className="text-[11px] font-semibold text-pink-600 uppercase mb-1">
                    Phase 5 sur 5
                  </p>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">
                    Page métier – Contexte spécifique (ex.: Syndicat / Éducation)
                  </h4>
                <p className="text-xs text-gray-600 mb-2">
                  Dernière phase 100% personnalisable selon le <strong>métier du client</strong>. Dans cet exemple
                  fictif, on parle du contexte syndicat / éducation, mais pour un autre client ce serait une autre
                  page métier (ex.: manufacturier, santé, retail...).
                </p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Questions spécifiques au métier (taille du syndicat, enjeux propres au réseau scolaire, etc.)</li>
                    <li>Enjeux métier critiques (communication aux membres, données sensibles, démocratie syndicale...)</li>
                    <li>Canaux de communication avec les membres / usagers</li>
                    <li>Contraintes réglementaires métier (Loi 25, conventions collectives, etc.)</li>
                    <li>Défis humains &amp; politiques (résistance au changement, calendrier spécifique, etc.)</li>
                  </ul>
                  <p className="text-[11px] text-gray-500 mt-2">
                    Chaque client aura sa propre page métier, stockée dans la même prise de besoin, mais avec un
                    contenu adapté à son industrie. C&apos;est cette couche qui permet à PRIsME de rester très
                    personnalisable sans exploser la complexité.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rapport' && templateId === 'politique-cybersecurite-v2' && (
            <div className="space-y-5 text-xs text-gray-700">
              {/* Bandeau titre */}
              <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 px-5 py-3 text-white">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-white/80 font-semibold">
                        Analyse de Besoins - Politique de Cybersécurité
                      </p>
                      <h3 className="text-lg font-bold">Client fictif – Cabinet d'avocats Desjardins & Associés</h3>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[10px]">
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        🏢 45 employés
                      </span>
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        🔒 NIST CSF 2.0
                      </span>
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        📋 Loi 25 / RGPD
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-white px-5 py-3 flex items-center justify-between text-[11px]">
                  <span className="text-gray-600">
                    Cabinet d'avocats • Données sensibles • Conformité réglementaire (mock)
                  </span>
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[9px]">
                      ✓
                    </span>
                    Prise de besoin complétée
                  </span>
                </div>
              </div>

              {/* Enjeux + objectifs */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-50 rounded-xl border border-red-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-red-900 mb-1">Défis de cybersécurité critiques</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Protection des données clients sensibles (dossiers juridiques)</li>
                    <li>Conformité Loi 25 et RGPD - obligations légales strictes</li>
                    <li>Risques de fuites de données (avocats, clients, témoins)</li>
                    <li>Accès à distance sécurisé pour télétravail</li>
                    <li>Gestion des identités et accès (partenaires externes)</li>
                    <li>Protection contre les ransomwares et phishing</li>
                    <li>Traçabilité et audit des accès aux dossiers</li>
                  </ul>
                </div>
                <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-emerald-900 mb-1">Objectifs de la politique</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Établir un cadre de cybersécurité aligné NIST CSF 2.0</li>
                    <li>Protéger les données sensibles et respecter la confidentialité</li>
                    <li>Conformité Loi 25, RGPD et obligations professionnelles</li>
                    <li>Formation et sensibilisation des employés</li>
                    <li>Gestion des accès et authentification renforcée</li>
                    <li>Politique BYOD claire et sécurisée</li>
                    <li>Gouvernance et audit régulier</li>
                  </ul>
                </div>
              </div>

              {/* Détails par phase - Style similaire au rapport PDTI */}
              {template && template.phases.map((phase, phaseIndex) => {
                const phaseNum = phaseIndex + 1;
                const colors = [
                  { border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-900' },
                  { border: 'border-orange-500', bg: 'bg-orange-50', text: 'text-orange-900' },
                  { border: 'border-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-900' },
                  { border: 'border-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-900' },
                ];
                const color = colors[phaseIndex] || colors[0];
                
                // Données mock pour chaque phase
                const mockData: Record<string, any> = {
                  'phase1': {
                    'entreprise': 'Cabinet d\'avocats Desjardins & Associés',
                    'enquete_securite_utilisateurs': 'Oui',
                    'formation_obligatoire_utilisateurs': 'Oui',
                    'groupes_utilisateurs_uniformes': 'Non - Groupes d\'utilisateurs différenciés',
                    'groupes_utilisateurs_detail': 'Direction (accès complet), Avocats associés (accès dossiers clients), Avocats employés (accès limités), Secrétaires juridiques (accès documents assignés), Consultants externes (accès temporaire projet)',
                  },
                  'phase2': {
                    'admin_postes_travail': 'Non',
                    'telechargement_tous_logiciels': 'Non',
                    'utilisation_tous_sites_web': 'Non',
                    'sites_web_autorises_interdits': 'Autoriser: SharePoint, Teams, Office 365, LexisNexis, Westlaw / Interdire: Sites de téléchargement, réseaux sociaux personnels, sites de streaming',
                    'methode_logiciels_autorises': 'Restriction technologique',
                    'utilisation_medias_sociaux': 'Oui - Avec restrictions',
                    'medias_sociaux_autorises': 'LinkedIn (marketing professionnel), Twitter (communication juridique)',
                    'medias_sociaux_interdits': 'Facebook personnel, Instagram personnel, TikTok',
                    'utilisation_personnelle_actifs_ti': 'Non',
                    'politique_byod': 'Oui - Avec restrictions',
                    'byod_conditions': 'MDM obligatoire, chiffrement des données, accès VPN uniquement, pas de stockage local de données clients',
                  },
                  'phase3': {
                    'authentification_mfa': 'Oui - Obligatoire',
                    'mfa_methodes': 'Application authentificatrice (Microsoft Authenticator), SMS (backup), Token physique (pour accès critiques)',
                    'gestion_mots_de_passe': 'Complexité élevée (12+ caractères, majuscules, minuscules, chiffres, symboles), rotation tous les 90 jours, pas de réutilisation des 12 derniers',
                    'politique_courriel': 'Chiffrement obligatoire pour données sensibles, protection anti-phishing avancée, filtrage des pièces jointes',
                    'gestion_identites': 'SSO avec Azure AD, provisioning automatique, désactivation automatique après 90 jours d\'inactivité',
                  },
                  'phase4': {
                    'restrictions_stockage_cloud': 'Pas de stockage cloud personnel (Dropbox, Google Drive personnel), utilisation uniquement de OneDrive Business approuvé',
                    'politique_ia': 'Utilisation contrôlée, pas de données clients dans IA publique (ChatGPT, etc.), utilisation uniquement d\'IA interne ou approuvée',
                    'conformite_audit': 'Audit annuel complet, documentation complète de toutes les politiques, traçabilité de tous les accès',
                    'formation_sensibilisation': 'Formation obligatoire annuelle, sensibilisation continue via infolettres, simulations de phishing trimestrielles',
                  },
                };

                const phaseKey = `phase${phaseNum}`;
                const phaseMockData = mockData[phaseKey] || {};

                return (
                  <div key={phase.id} className={`bg-white rounded-xl border-2 ${color.border} p-5 shadow-sm`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-lg ${color.bg} flex items-center justify-center`}>
                        <span className={`text-sm font-bold ${color.text}`}>{phaseNum}</span>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase">
                          Phase {phaseNum} sur {template.phases.length}
                        </p>
                        <h4 className={`text-base font-bold ${color.text} mb-1`}>{phase.title}</h4>
                        {phase.description && (
                          <p className="text-xs text-gray-600">{phase.description}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-4 mt-4">
                      {phase.questions.map((question) => {
                        const answer = phaseMockData[question.id];
                        if (!answer) return null;

                        return (
                          <div key={question.id} className="border-l-2 border-gray-200 pl-3">
                            <h5 className="text-xs font-semibold text-gray-800 mb-1">
                              {question.label}
                            </h5>
                            {Array.isArray(answer) ? (
                              <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
                                {answer.map((item: string, idx: number) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-gray-700 leading-relaxed">{answer}</p>
                            )}
                            {question.helpText && (
                              <p className="text-[10px] text-gray-500 italic mt-1">
                                {question.helpText.substring(0, 100)}...
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 flex items-start gap-3">
                <div className="mt-1">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold">
                    ✅
                  </span>
                </div>
                <div className="text-[11px] text-emerald-900">
                  <p className="font-semibold mb-1">Prise de besoin complétée</p>
                  <p>
                    Tous les besoins ont été structurés dans PRIsME. On peut maintenant générer la{' '}
                    <strong>Politique de cybersécurité</strong> complète alignée sur NIST CSF 2.0, ITIL 4, COBIT 2019 et ISO 27001, 
                    en utilisant cette prise de besoin comme base commune.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rapport' && isClient2026Template && (
            <div className="space-y-5 text-xs text-gray-700">
              <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-emerald-600 via-bz-blue to-indigo-700 px-5 py-3 text-white">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-white/80 font-semibold">
                        {isClient2026AnalysisTemplate
                          ? 'Document à produire - Analyse & recommandations'
                          : 'Document à produire - Tableau de prise de besoin'}
                      </p>
                      <h3 className="text-lg font-bold">
                        {isClient2026AnalysisTemplate
                          ? 'Prise de besoin - Maturité et besoins client - Analyse & recommandations'
                          : 'Prise de besoin - Maturité et besoins client (tableau)'}
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[10px]">
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        6 piliers
                      </span>
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        Priorités client / BZ
                      </span>
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        Solutions et risques
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-white px-5 py-3 flex items-center justify-between text-[11px]">
                  <span className="text-gray-600">
                        {isClient2026AnalysisTemplate
                          ? 'Formulaire source: Prise de besoin - Maturité et besoins client • Sortie: analyse narrative et recommandations'
                          : 'Formulaire source: Prise de besoin - Maturité et besoins client • Sortie: tableau client exploitable'}
                  </span>
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[9px]">
                      ✓
                    </span>
                    Contenu lié au document
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Esprit, destinataire et usage</h4>
                  <div className="space-y-2 text-xs text-gray-700">
                    <p>
                      <strong>Esprit:</strong> rendre visible ce qui a été compris pendant la prise de besoin et le
                      convertir en tableau de décisions exploitable.
                    </p>
                    <p>
                      <strong>Livré à:</strong> client, Directeur TI BZ, équipe de livraison et toute personne qui doit
                      produire ou valider les livrables qui découlent de la rencontre.
                    </p>
                    <p>
                      <strong>Pourquoi:</strong> aligner la compréhension, les priorités, les prochaines actions et la
                      valeur attendue avant de produire les documents plus détaillés.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Ce que le document doit contenir</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Les réponses concrètes du client pour chaque ligne applicable.</li>
                    <li>La façon de confirmer que le problème est réglé.</li>
                    <li>Les priorités côté client et côté Directeur TI BZ.</li>
                    <li>Les objectifs SMART découlant des réponses.</li>
                    <li>Les solutions technologiques, processus et humaines.</li>
                    <li>Les coûts, contraintes, risques, mitigations et besoins associés.</li>
                  </ul>
                </div>
                <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 shadow-sm md:col-span-2">
                  <h4 className="text-sm font-semibold text-amber-900 mb-2">Règles de qualité</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Ne pas inventer de faits client: déductions acceptées seulement si elles sont raisonnables.</li>
                    <li>Garder les cases vides ou marquées « Non précisé » lorsque l'information manque.</li>
                    <li>Prioriser en Haute / Moyenne / Basse quand la valeur doit être claire pour BZ.</li>
                    <li>Conserver une formulation actionnable, pas seulement descriptive.</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm overflow-x-auto">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Exemple de ligne dans le document produit</h4>
                <table className="min-w-full text-[11px] border border-gray-200">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      {['Pilier', 'Question', 'Réponse client', 'Priorité', 'Objectif SMART', 'Solution', 'Risque / mitigation'].map((h) => (
                        <th key={h} className="border border-gray-200 px-3 py-2 text-left font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="border border-gray-200 px-3 py-2 align-top">{`Pilier 1 — ${CLIENT_2026_AXE_TITLES[1]}`}</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Avez-vous un comité TI?</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Oui, mais la cadence et les suivis doivent être structurés.</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Haute</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Mettre en place une cadence documentée d'ici 90 jours.</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Rituel de gouvernance, ordre du jour, registre d'actions et suivi PRIsME.</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Risque: suivi informel. Mitigation: propriétaire, échéances et preuve de fermeture.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 flex items-start gap-3">
                <div className="mt-1">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold">
                    ✓
                  </span>
                </div>
                <div className="text-[11px] text-emerald-900">
                  <p className="font-semibold mb-1">Lien formulaire → document</p>
                  <p>
                    Quand la prise de besoin client 2026 est complétée, PRIsME peut produire le tableau final en
                    reprenant les lignes applicables et en y ajoutant l'analyse BZ: priorités, objectifs, solutions,
                    risques, mitigations et besoins à adresser.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rapport' && isKmiTemplate && (
            <div className="space-y-5 text-xs text-gray-700">
              <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-blue-700 via-bz-blue to-indigo-700 px-5 py-3 text-white">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-white/80 font-semibold">
                        Document à produire - Diagnostic de maturité actuelle
                      </p>
                      <h3 className="text-lg font-bold">PRIsME - KMI (diagnostic 6 piliers)</h3>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[10px]">
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        6 piliers
                      </span>
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        Scores 0 à 5
                      </span>
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        Spider PRIsME Client
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-white px-5 py-3 flex items-center justify-between text-[11px]">
                  <span className="text-gray-600">
                    Formulaire source: questionnaire KMI • Sortie: diagnostic exécutif et lecture de maturité
                  </span>
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[9px]">
                      ✓
                    </span>
                    Contenu lié au document
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Esprit, destinataire et usage</h4>
                  <div className="space-y-2 text-xs text-gray-700">
                    <p>
                      <strong>Esprit:</strong> objectiver la maturité TI actuelle pour passer d'une impression générale
                      à une lecture comparable par pilier.
                    </p>
                    <p>
                      <strong>Livré à:</strong> direction client, responsables TI, Directeur TI BZ, spécialistes et
                      personnes qui doivent prioriser les chantiers.
                    </p>
                    <p>
                      <strong>Pourquoi:</strong> identifier les écarts critiques, justifier les recommandations et
                      alimenter le spider de maturité actuelle dans PRIsME Client.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Ce que le document doit contenir</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Un résumé exécutif de la maturité actuelle.</li>
                    <li>La moyenne globale et les moyennes par pilier.</li>
                    <li>Les piliers forts, faibles et incohérents.</li>
                    <li>Les questions ou preuves importantes qui expliquent les scores.</li>
                    <li>Les risques d'affaires ou opérationnels associés aux faibles scores.</li>
                    <li>Les recommandations prioritaires et les prochains livrables à produire.</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm overflow-x-auto">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Exemple de lecture dans le document produit</h4>
                <table className="min-w-full text-[11px] border border-gray-200">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      {['Pilier', 'Score moyen', 'Lecture BZ', 'Risque principal', 'Action recommandée'].map((h) => (
                        <th key={h} className="border border-gray-200 px-3 py-2 text-left font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="border border-gray-200 px-3 py-2 align-top">Gouvernance, Stratégie & Organisation</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">2.1 / 5</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Pratiques présentes mais informelles; décisions TI peu suivies.</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Priorités changeantes, investissements difficiles à justifier.</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Mettre en place un comité TI, un registre de décisions et une roadmap priorisée.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-3 py-2 align-top">Cybersécurité, Risques & Conformité</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">1.8 / 5</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Contrôles partiels; posture difficile à démontrer.</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Exposition aux incidents, audits et obligations réglementaires.</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Produire un plan cyber priorisé et traiter les contrôles de base.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-blue-950 mb-2">Règles de qualité</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Ne pas présenter un score comme une vérité absolue: l'expliquer par les réponses et preuves.</li>
                    <li>Ne pas recommander un chantier sans lien avec un écart de maturité ou un risque.</li>
                    <li>Indiquer les réponses manquantes ou insuffisamment justifiées.</li>
                    <li>Différencier maturité actuelle, maturité cible et recommandation BZ.</li>
                  </ul>
                </div>
                <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-emerald-950 mb-2">Lien formulaire → document</h4>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    Quand le questionnaire KMI est complété, PRIsME calcule les moyennes par pilier, génère le contexte
                    du diagnostic, conserve les scores dans les métadonnées du document et peut afficher le spider de
                    maturité actuelle dans PRIsME Client.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rapport' && templateId !== 'politique-cybersecurite-v2' && !isClient2026Template && !isKmiTemplate && (
            <div className="space-y-5 text-xs text-gray-700">
              {/* Bandeau titre */}
              <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-emerald-500 via-bz-blue to-indigo-600 px-5 py-3 text-white">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-white/80 font-semibold">
                        Analyse de Besoins TI – Mock PRIsME
                      </p>
                      <h3 className="text-lg font-bold">Client fictif – Syndicat de l&apos;enseignement</h3>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[10px]">
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        ⏱ Horizon 1–2 ans
                      </span>
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        👥 Moins de 25 employés
                      </span>
                      <span className="px-3 py-1 rounded-full bg-white/15 border border-white/30">
                        🧩 Maturité TI: Intermédiaire
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-white px-5 py-3 flex items-center justify-between text-[11px]">
                  <span className="text-gray-600">
                    OBNL – Syndicat • 8 500 membres • Infogérance TI complète (mock)
                  </span>
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[9px]">
                      ✓
                    </span>
                    Prise de besoin complétée
                  </span>
                </div>
              </div>

              {/* Enjeux + objectifs */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-50 rounded-xl border border-red-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-red-900 mb-1">Défi principal & enjeux critiques</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Infrastructure vieillissante (serveur 8 ans, QNAP obsolète)</li>
                    <li>Aucune sauvegarde cloud – risque majeur de perte de données</li>
                    <li>Cybersécurité insuffisante – vulnérabilité aux ransomwares</li>
                    <li>Migration nécessaire vers Microsoft 365 Business Premium</li>
                    <li>Communication efficace avec 8 500 membres</li>
                    <li>Conformité Loi 25 et protection des données sensibles</li>
                    <li>Continuité de service en contexte de négociation / grève</li>
                  </ul>
                </div>
                <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-emerald-900 mb-1">Objectifs 2026 (TI & affaires)</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Migration complète vers M365 (Q1)</li>
                    <li>Déploiement sauvegardes cloud + copie hors ligne (Q1)</li>
                    <li>Remplacement infrastructure réseau (Q2)</li>
                    <li>Formation cybersécurité complète (Q2)</li>
                    <li>Conformité Loi 25 &amp; RGPD (Q2)</li>
                    <li>Optimisation processus &amp; automatisation (Q3)</li>
                    <li>Évaluation décommissionnement serveur physique (Q3)</li>
                    <li>Bilan annuel et Plan Directeur 2026 (Q4)</li>
                  </ul>
                </div>
              </div>

              {/* Objectifs & métriques */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Objectifs stratégiques &amp; métriques</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-[11px] font-semibold text-gray-800 mb-1">📈 Objectifs d&apos;affaires</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Amélioration de l&apos;efficacité opérationnelle</li>
                      <li>Transformation numérique</li>
                      <li>Amélioration de l&apos;expérience membre</li>
                      <li>Conformité réglementaire</li>
                      <li>Réduction des coûts</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-800 mb-1">💻 Objectifs TI</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Moderniser l&apos;infrastructure</li>
                      <li>Améliorer la sécurité &amp; la résilience</li>
                      <li>Migration cloud (M365 Premium)</li>
                      <li>Automatisation des processus</li>
                      <li>Mobilité &amp; travail hybride</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-800 mb-1">🎯 Métriques de succès</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>RPO ≤ 4 h, RTO ≤ 1 h, tests de restauration 100 %/trimestre</li>
                      <li>MFA 100 %, phishing &lt; 3 %, patching ≤ 14 jours</li>
                      <li>CSAT support ≥ 4,5/5, MTTR −25 %</li>
                      <li>TCO −10–15 %, ROI positif à 12 mois</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Contexte métier syndicat / éducation */}
              <div className="bg-fuchsia-50 rounded-xl border border-fuchsia-200 p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-fuchsia-900 mb-1">
                  Page métier – Contexte Syndicat / Éducation
                </h4>
                <p className="text-xs text-gray-600 mb-2">
                  Cette section illustre comment la page métier vient enrichir la prise de besoin pour un contexte
                  spécifique (ici, un syndicat du milieu de l&apos;éducation).
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>5 000 membres et plus, communication avec l&apos;ensemble du membership</li>
                  <li>
                    Enjeux: communication, protection des données personnelles, conventions collectives numériques,
                    continuité de service en période de grève/négociation
                  </li>
                  <li>
                    Canaux: infolettres, AG physiques, site web, réseaux sociaux, webinaires / rencontres virtuelles
                  </li>
                  <li>
                    Contraintes: Loi 25, gestion des griefs, collaboration avec commissions scolaires/établissements
                  </li>
                  <li>
                    Défis humains &amp; politiques: structure complexe, résistance au changement, calendrier scolaire /
                    syndical
                  </li>
                </ul>
              </div>

              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 flex items-start gap-3">
                <div className="mt-1">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold">
                    ✅
                  </span>
                </div>
                <div className="text-[11px] text-emerald-900">
                  <p className="font-semibold mb-1">Prise de besoin complétée</p>
                  <p>
                    Tous les besoins ont été structurés dans PRIsME. On peut maintenant générer le{' '}
                    <strong>Cadre Référentiel BZ</strong>, la <strong>Feuille de route annuelle</strong> et le{' '}
                    <strong>Plan Directeur</strong> en utilisant cette prise de besoin comme base commune.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
